import React, { useMemo, useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout.jsx';
import { resolveShopContext } from '../common/shopContext';
import { getSiteTheme, normalizeTemplateKey } from '../common/websiteTemplates';
import { addToCart, loadCart } from '../common/shopUtils';
import { formatMoney, getEffectiveProductPricing } from '../common/shopPricing';
import { isPartnerComingSoon, isPartnerMaintenance } from '../common/partnerUtils';
import PartnerUpsellPage from './PartnerUpsellPage';
import PartnerMaintenancePage from './PartnerMaintenancePage';
import PartnerComingSoonPage from './PartnerComingSoonPage';
import { getLocalizedShopItem } from '../common/shopContent';

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const stripHtml = (value) => String(value || '').replace(/<[^>]*>/g, '').trim();

const getCoverImage = (product) => {
  if (!product) return '';
  const direct = product.image || product.thumbnail || '';
  if (direct) return direct;
  const gallery = Array.isArray(product.images) ? product.images.filter(Boolean) : [];
  return gallery[0] || '';
};

const isServiceItem = (product) => product?.type === 'service';

const isOutOfStockItem = (product) => {
  if (!product) return false;
  if (isServiceItem(product)) return false;
  if (product.stock === undefined || product.stock === null || product.stock === '') return false;
  const stock = Number(product.stock || 0);
  return Number.isFinite(stock) && stock <= 0;
};

const getCartCount = (scopeKey) => {
  const cart = loadCart(scopeKey);
  return Object.values(cart || {}).reduce((sum, qty) => sum + Math.max(0, Number(qty || 0)), 0);
};

export default function ShopCategory() {
  const navigate = useNavigate();
  const location = useLocation();
  const { partnerSlug, categoryId } = useParams();
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const { t, i18n } = useTranslation();

  const shopContext = useMemo(
    () =>
      resolveShopContext(settings, {
        partnerSlug,
        locale: i18n.language,
        pathname: location.pathname,
      }),
    [settings, partnerSlug, i18n.language, location.pathname]
  );

  const { partner, cartScopeKey, paths, websiteContent, prefixPartnerLink } = shopContext;
  const isAdmin = auth?.profile?.usertype === 'admin' || auth?.profile?.usertype === 'fleetadmin';

  const templateKey = normalizeTemplateKey(websiteContent.template || 'classic');
  const theme = getSiteTheme(templateKey, websiteContent.appearance || {});
  const shop = websiteContent.shop || {};
  const currency = shop.currency || 'USD';
  const shopEnabled = shop.enabled !== false;
  const campaigns = useMemo(() => (Array.isArray(shop.campaigns) ? shop.campaigns : []), [shop.campaigns]);

  const categories = useMemo(() => {
    const list = Array.isArray(shop.categories) ? shop.categories : [];
    return list
      .filter((cat) => cat && cat.published !== false && cat.id)
      .map((cat) => getLocalizedShopItem(cat, i18n.language));
  }, [shop.categories, i18n.language]);

  const category = useMemo(() => {
    const key = String(categoryId || '').trim();
    if (!key) return null;
    return categories.find((cat) => String(cat.id) === key || String(cat.slug || '') === key) || null;
  }, [categoryId, categories]);

  const products = useMemo(() => {
    const list = Array.isArray(shop.products) ? shop.products.filter((p) => p && p.published !== false) : [];
    return list
      .map((p) => getLocalizedShopItem(p, i18n.language))
      .filter((p) => !category?.id || String(p?.category || '') === String(category.id));
  }, [shop.products, i18n.language, category?.id]);

  const pricedProducts = useMemo(() => {
    return products.map((product) => ({
      product,
      pricing: getEffectiveProductPricing(product, campaigns),
    }));
  }, [products, campaigns]);

  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sort, setSort] = useState('featured');

  const filteredProducts = useMemo(() => {
    const q = normalizeText(query);
    return pricedProducts.filter(({ product }) => {
      const type = product?.type === 'service' ? 'service' : 'product';
      if (typeFilter !== 'all' && type !== typeFilter) return false;
      if (!q) return true;
      const haystack = [
        product.title,
        product.description,
        product.category,
        product.tags,
        product.id,
        product.slug,
      ]
        .filter(Boolean)
        .map(normalizeText)
        .join(' ');
      return haystack.includes(q);
    });
  }, [query, pricedProducts, typeFilter]);

  const sortedProducts = useMemo(() => {
    const next = [...filteredProducts];
    if (sort === 'featured') {
      next.sort((a, b) => {
        const af = a.product?.featured ? 1 : 0;
        const bf = b.product?.featured ? 1 : 0;
        if (bf !== af) return bf - af;
        return String(a.product?.title || '').localeCompare(String(b.product?.title || ''));
      });
    }
    if (sort === 'price_low') next.sort((a, b) => Number(a.pricing.finalPrice || 0) - Number(b.pricing.finalPrice || 0));
    if (sort === 'price_high') next.sort((a, b) => Number(b.pricing.finalPrice || 0) - Number(a.pricing.finalPrice || 0));
    return next;
  }, [filteredProducts, sort]);

  const [cartCount, setCartCount] = useState(() => getCartCount(cartScopeKey));
  useEffect(() => {
    setCartCount(getCartCount(cartScopeKey));
  }, [cartScopeKey]);

  const gateInactive = Boolean(partner?.inactive);
  const gateComingSoon = Boolean(partner && isPartnerComingSoon(partner) && !isAdmin);
  const gateMaintenance = Boolean(partner && isPartnerMaintenance(partner) && !isAdmin);

  if (gateInactive) {
    return <PartnerUpsellPage partner={partner} />;
  }

  if (gateComingSoon) {
    return <PartnerComingSoonPage partner={partner} />;
  }

  if (gateMaintenance) {
    return <PartnerMaintenancePage partner={partner} />;
  }

  if (!shopEnabled) {
    return (
      <PublicLayout title={t('shop') || 'Shop'} partner={partner} homePath={paths.home} bookingPath={paths.booking}>
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 6, md: 8 } }}>
          <Typography sx={{ fontWeight: 900, color: theme.text, fontSize: 28 }}>
            {t('shop_disabled') || 'Shop is disabled'}
          </Typography>
          <Typography sx={{ color: theme.muted, mt: 1 }}>
            {t('shop_disabled_help') || 'Please check back later.'}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(paths.shop)}
            sx={{ mt: 3, borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
          >
            {t('back_to_shop') || 'Back to shop'}
          </Button>
        </Box>
      </PublicLayout>
    );
  }

  if (!category) {
    return (
      <PublicLayout title={t('shop') || 'Shop'} partner={partner} homePath={paths.home} bookingPath={paths.booking}>
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 6, md: 8 } }}>
          <Alert severity="warning">{t('category_not_found') || 'Category not found.'}</Alert>
          <Button
            variant="contained"
            onClick={() => navigate(paths.shop)}
            sx={{ mt: 3, borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
          >
            {t('back_to_shop') || 'Back to shop'}
          </Button>
        </Box>
      </PublicLayout>
    );
  }

  const categoryTitle = category?.title || category?.id || t('category') || 'Category';
  const categoryDescription = category?.description || '';
  const descriptionText = stripHtml(categoryDescription);

  const handleAdd = (event, product) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!product?.id) return;
    if (isOutOfStockItem(product)) return;
    addToCart(product.id, 1, cartScopeKey);
    setCartCount(getCartCount(cartScopeKey));
  };

  return (
    <PublicLayout
      title={categoryTitle}
      description={stripHtml(categoryDescription)}
      image={category?.image}
      partner={partner}
      homePath={paths.home}
      bookingPath={paths.booking}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 5, md: 7 } }}>
        <Box
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            border: `1px solid ${theme.border}`,
            bgcolor: theme.darkSectionBg || '#0b0b0b',
            color: theme.darkSectionText || '#fff',
            px: { xs: 3, md: 5 },
            py: { xs: 4, md: 5 },
            position: 'relative',
          }}
        >
          {category.image ? (
            <Box
              component="img"
              src={category.image}
              alt={categoryTitle}
              sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background:
                  'radial-gradient(900px 260px at 20% 20%, rgba(56,189,248,0.35), transparent 60%), radial-gradient(800px 260px at 80% 30%, rgba(34,197,94,0.25), transparent 60%), linear-gradient(135deg, #0b1120 0%, #111827 60%)',
              }}
            />
          )}
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.45)' }} />
          <Stack spacing={1.5} sx={{ position: 'relative', zIndex: 1, maxWidth: 720 }}>
            <Typography sx={{ fontWeight: 900, fontSize: { xs: 28, md: 40 }, letterSpacing: -1 }}>
              {categoryTitle}
            </Typography>
            {categoryDescription ? (
              <Typography
                sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.7 }}
                dangerouslySetInnerHTML={{ __html: categoryDescription }}
              />
            ) : descriptionText ? (
              <Typography sx={{ color: 'rgba(255,255,255,0.8)' }}>{descriptionText}</Typography>
            ) : null}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
              <Button
                variant="contained"
                onClick={() => navigate(paths.shop)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
              >
                {t('back_to_shop') || 'Back to shop'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate(paths.cart)}
                startIcon={<ShoppingCartRoundedIcon />}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900, color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}
              >
                {(t('cart') || 'Cart') + ` (${cartCount})`}
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mt: 3 }}>
          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('search_products') || 'Search products…'}
            size="small"
            sx={{ minWidth: { xs: '100%', md: 320 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            label={t('sort') || 'Sort'}
            size="small"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            sx={{ minWidth: { xs: '100%', md: 200 } }}
          >
            <MenuItem value="featured">{t('featured') || 'Featured'}</MenuItem>
            <MenuItem value="price_low">{t('price_low_high') || 'Price: low → high'}</MenuItem>
            <MenuItem value="price_high">{t('price_high_low') || 'Price: high → low'}</MenuItem>
          </TextField>

          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Chip
              label={t('all') || 'All'}
              onClick={() => setTypeFilter('all')}
              variant={typeFilter === 'all' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 900 }}
            />
            <Chip
              label={t('products') || 'Products'}
              onClick={() => setTypeFilter('product')}
              variant={typeFilter === 'product' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 900 }}
            />
            <Chip
              label={t('services') || 'Services'}
              onClick={() => setTypeFilter('service')}
              variant={typeFilter === 'service' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 900 }}
            />
          </Stack>
        </Stack>

        {!sortedProducts.length ? (
          <Box sx={{ mt: 4, p: 3, borderRadius: 2, border: `1px solid ${theme.border}`, bgcolor: theme.sectionAltBg }}>
            <Typography sx={{ fontWeight: 900, color: theme.text }}>
              {t('no_products') || 'No products found'}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.muted, mt: 0.5 }}>
              {t('no_products_help') || 'Try a different filter or check back later.'}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2.5} sx={{ mt: 2 }}>
            {sortedProducts.map(({ product, pricing }) => (
              <Grid key={product.id} item xs={12} sm={6} md={4}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    borderColor: theme.border,
                    bgcolor: theme.cardBg,
                    height: '100%',
                  }}
                >
                  <CardActionArea onClick={() => navigate(prefixPartnerLink(`/shop/${encodeURIComponent(product.id)}`))}>
                    <Box
                      sx={{
                        height: 190,
                        position: 'relative',
                        bgcolor: theme.sectionAltBg,
                        overflow: 'hidden',
                      }}
                    >
                      {getCoverImage(product) ? (
                        <Box component="img" src={getCoverImage(product)} alt={product.title} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            background:
                              'radial-gradient(900px 260px at 20% 20%, rgba(56,189,248,0.25), transparent 60%), radial-gradient(800px 260px at 80% 30%, rgba(34,197,94,0.20), transparent 60%), linear-gradient(135deg, #0b1120 0%, #111827 60%)',
                          }}
                        />
                      )}
                      <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.12)' }} />
                      {isServiceItem(product) ? (
                        <Chip
                          label={t('service') || 'Service'}
                          size="small"
                          sx={{ position: 'absolute', top: 12, right: 12, fontWeight: 900, bgcolor: theme.cardBg }}
                          variant="outlined"
                        />
                      ) : null}
                      {isOutOfStockItem(product) ? (
                        <Chip
                          label={t('out_of_stock') || 'Out of stock'}
                          size="small"
                          sx={{ position: 'absolute', bottom: 12, left: 12, fontWeight: 900, bgcolor: theme.cardBg }}
                          color="warning"
                          variant="outlined"
                        />
                      ) : null}
                    </Box>
                    <CardContent>
                      <Typography sx={{ fontWeight: 950, color: theme.text, fontSize: 16 }}>
                        {product.title}
                      </Typography>
                      {product.description ? (
                        <Typography variant="body2" sx={{ color: theme.muted, mt: 0.5 }}>
                          {stripHtml(product.description).slice(0, 120)}
                        </Typography>
                      ) : null}
                      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mt: 1 }}>
                        <Typography sx={{ fontWeight: 950, color: theme.text }}>
                          {formatMoney(pricing.finalPrice, currency)}
                        </Typography>
                        {pricing.compareAtPrice ? (
                          <Typography sx={{ color: theme.muted, fontWeight: 800, textDecoration: 'line-through' }}>
                            {formatMoney(pricing.compareAtPrice, currency)}
                          </Typography>
                        ) : null}
                      </Stack>
                      <Button
                        variant="contained"
                        onClick={(event) => handleAdd(event, product)}
                        disabled={isOutOfStockItem(product)}
                        sx={{
                          mt: 1.5,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 900,
                          bgcolor: theme.accent || '#111827',
                          '&:hover': { bgcolor: theme.accent || '#0b1224' },
                        }}
                      >
                        {t('add_to_cart') || 'Add to cart'}
                      </Button>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </PublicLayout>
  );
}
